import { isRejectedWithValue } from '@reduxjs/toolkit';
import { isNil } from 'lodash';
import { ERROR_STATUS, handleClientError, handleServerError, reportError, } from 'shared';
import { toastError } from 'utils';
function reportHoneybadgerError(action) {
    const errorMessage = action?.payload?.data?.message;
    const errorCode = action?.payload?.data?.error_code;
    const endpointName = action?.meta?.arg?.endpointName;
    const status = action?.meta?.baseQueryMeta?.response?.status;
    reportError('apiError', {
        componentName: 'apiMiddlewareRTK',
        errType: 'API_ERROR',
        errURL: window.location.href,
        errMsg: errorMessage ?? 'An unexpected error occurred',
        errCode: errorCode,
        context: {
            status, //should be 500 or 401 or 403 or 404
            endpointName,
            errorCode, //should be 401, 403, 404, 500, 503, 504
            originalArgs: action?.meta?.arg?.originalArgs, //should be the original arguments passed to the API call
            requestId: action?.meta?.requestId, //should be the request ID from the server
            httpMethod: action?.meta?.baseQueryMeta?.request?.method,
            url: action?.meta?.baseQueryMeta?.request?.url,
        },
    });
}
const SILENT_ERROR_ENDPOINTS = new Set([
    'getConsumerApplicationsV2AccountFinderLookupVerificationToken',
    'getConsumerApplicationsV2TransactionsLookupVerificationToken',
    'getConsumerApplicationsV2CreditApplicationsLookupVerificationToken',
    'postServicingV1InstrumentsInstrumentIdAgreementsTermsAchAuthorization',
    // "Find my application" lookup: the API is expected to error with a 422 when
    // no credit applications are found, which is a normal "no match" for this
    // flow, so it must not trigger a client-error redirect/toast.
    'getConsumerApplicationsV1CreditApplicationsStatusStatus',
]);
// Store link campaign attribution is best-effort metadata. Any failure here
// must leave the consumer on their intended page -- no toast, no client-error
// redirect -- so these still report to HyperDX but render no error UI.
const SILENT_UI_ERROR_ENDPOINTS = new Set([
    'getConsumerApplicationsV1LinkOtlTokenValidate',
]);
const apiMiddlewareRTK = () => (next) => (action) => {
    if (!action.type.includes('merchantPortalApi') &&
        !action.type.includes('consumerPortalApi') &&
        !action.type.includes('internalPortalApi') &&
        !action.type.includes('abTestsApi') &&
        !action.type.includes('affWebhooksApi') &&
        !action.type.includes('servicingApi')) {
        return next(action);
    }
    if (isRejectedWithValue(action)) {
        const endpointName = action?.meta?.arg?.endpointName || '';
        if (SILENT_ERROR_ENDPOINTS.has(endpointName)) {
            return next(action);
        }
        const clientErrors = [401, 403, 404];
        const status = action?.payload?.status;
        reportHoneybadgerError(action);
        if (SILENT_UI_ERROR_ENDPOINTS.has(endpointName))
            return next(action);
        if (isNil(status))
            return handleClientError(ERROR_STATUS.API_DOWN);
        if (clientErrors.includes(status))
            return handleClientError(status.toString());
        if (status === 503)
            return handleServerError();
        const errorMessage = action?.payload?.data?.message;
        toastError(errorMessage ?? 'An unexpected error occurred');
    }
    return next(action);
};
export default apiMiddlewareRTK;
